import { EventEmitter, Injectable, OnDestroy, inject } from '@angular/core';
import {
  CompiereNotification,
  CompiereNotificationDataIcon,
  CompiereNotificationPriority,
  CompiereNotificationType,
  CompiereUINotificationWS,
} from '@compiere-ws/models/compiere-notification-json';
import { CompiereNotificationService } from '@compiere-ws/services/compiere-notification/compiere-notification.service';
import { PrintReportService } from '@compiere-ws/services/compiere-print-report/print-report.service';
import { SocketService } from '@compiere-ws/services/socket/socket.service';
import { Global } from '@iupics-manager/models/global-var';
import { DateUtils } from '@iupics-util/tools/date.utils';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SecurityManagerService } from '../security-manager/security-manager.service';
import { SessionService } from '../session/session.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationManagerService implements OnDestroy {
  onChangeRoleChannel = new EventEmitter<any>();
  onRoleChanged = new EventEmitter<any>();
  private syncWaitingNotifications: {
    [key: string]: Subject<CompiereNotification>;
  } = {};

  private roleSub: Subscription;
  /** jobs that never answer: their waiters get an error after this delay instead of waiting forever */
  static readonly SYNC_TIMEOUT_MS = 30 * 60 * 1000;
  #syncTimers: { [channelId: string]: ReturnType<typeof setTimeout> } = {};

  constructor(
    private printReportService: PrintReportService,
    private messageService: MessageService,
    private compiereNotificationService: CompiereNotificationService,
    private socketService: SocketService,
    private connectorService: SecurityManagerService,
    private translateService: TranslateService
  ) {
    // role switch or logout: the jobs of the previous session will not be answered on the new channel
    inject(SessionService).reset$.subscribe(() => this.#cancelWaits());
  }

  #cancelWaits(): void {
    for (const channelId of Object.keys(this.syncWaitingNotifications)) {
      const waiting = this.syncWaitingNotifications[channelId];
      this.#endWait(channelId);
      waiting.error(new Error('Session changed'));
    }
    this.roleSub?.unsubscribe();
    this.roleSub = undefined;
  }

  ngOnDestroy(): void {
    this.roleSub?.unsubscribe();
    Object.values(this.#syncTimers).forEach((t) => clearTimeout(t));
  }

  getNotificationsByType(
    type: string,
    offset: number,
    limit: number,
    onlyUnread: boolean
  ): Observable<CompiereNotification[]> {
    return this.compiereNotificationService
      .getNotificationsByType(type, offset, limit, onlyUnread)
      .pipe(map((notifications) => this.initNotificationDatas(notifications)));
  }

  getNotificationsCounts(): Observable<CompiereUINotificationWS> {
    return this.compiereNotificationService.getNotificationsCounts();
  }

  handleNotification(notif: CompiereNotification): Observable<CompiereNotification> {
    return this.compiereNotificationService.handleNotification(notif);
  }

  handleAllNotification(type: CompiereNotificationType): Observable<CompiereNotification> {
    return this.compiereNotificationService.handleAllNotification(type);
  }

  closeNotification(notif: CompiereNotification): Observable<CompiereNotification> {
    return this.compiereNotificationService.closeNotification(notif);
  }

  closeAllNotification(type: CompiereNotificationType, onlyUnread: boolean): Observable<CompiereNotification> {
    return this.compiereNotificationService.closeAllNotification(type, onlyUnread);
  }

  changeRoleChannel(previous: number, next: number): void {
    this.onChangeRoleChannel.emit({ previousRole: previous, nextRole: next });
  }

  roleChanged(): void {
    this.onRoleChanged.emit();
  }

  popNotification(...notifications: CompiereNotification[]): void {
    for (const notification of notifications) {
      this.messageService.add({
        summary: notification.title,
        detail: notification.summary,
        data: { notification: notification, type: 'Notification' },
      });
    }
  }

  /**
   * Waits for the notification of one job (report, export, e-mail attachment). Several jobs can wait at once:
   * one subscription to the role channel dispatches by channel id (a new wait used to cancel the previous one,
   * whose spinner then never stopped). Emits once and completes, or errors after SYNC_TIMEOUT_MS.
   */
  syncWithNotification(channelId: string): Subject<CompiereNotification> {
    const waiting = new Subject<CompiereNotification>();
    this.syncWaitingNotifications[channelId] = waiting;
    clearTimeout(this.#syncTimers[channelId]);
    this.#syncTimers[channelId] = setTimeout(() => {
      if (this.syncWaitingNotifications[channelId] === waiting) {
        this.#endWait(channelId);
        waiting.error(new Error('No answer for ' + channelId));
      }
    }, NotificationManagerService.SYNC_TIMEOUT_MS);
    this.#listenRoleChannel();
    return waiting;
  }

  /** one subscription while jobs wait (a role switch closes the channel: the next wait subscribes again) */
  #listenRoleChannel(): void {
    if (this.roleSub && !this.roleSub.closed) return;
    this.roleSub = this.socketService.enableRoleNotifications().subscribe((notification) => {
      const foundChannelId = (notification as CompiereNotification).processChannelId
        ? (notification as CompiereNotification).processChannelId
        : (notification as CompiereNotification).processParams?.channel_id;
      const waiting = foundChannelId ? this.syncWaitingNotifications[foundChannelId] : undefined;
      if (waiting) {
        this.#endWait(foundChannelId);
        waiting.next(notification);
        waiting.complete();
      }
    });
  }

  #endWait(channelId: string): void {
    delete this.syncWaitingNotifications[channelId];
    clearTimeout(this.#syncTimers[channelId]);
    delete this.#syncTimers[channelId];
    if (!Object.keys(this.syncWaitingNotifications).length) {
      this.roleSub?.unsubscribe();
      this.roleSub = undefined;
    }
  }

  downloadReport(url: string): void {
    this.printReportService
      .downloadReport(url)
      .pipe(take(1))
      .subscribe((response) =>
        setTimeout(() => {
          Global.downloadFile(response);
        }, 100)
      );
  }

  initNotificationDatas(list: CompiereNotification[]): any {
    return list.map((notif) => this.initNotifData(notif));
  }

  initNotifData(notif: CompiereNotification): any {
    return {
      ...notif,
      data: notif.data || {
        icons: this.getIcons(notif),
        readAbleDate: this.getReadableDate(notif),
        priorityData: this.getPriorityData(notif),
      },
    };
  }

  getPriorityData(notif: CompiereNotification): any {
    if (!notif.priority) {
      return {};
    }
    let priorityData;
    switch (notif.priority) {
      case CompiereNotificationPriority.IMPORTANT:
        priorityData = {
          priorityTitle: this.translateService.instant('notification.priority.critical'),
          priorityClass: 'critical',
        };
        break;
      case CompiereNotificationPriority.HIGH:
        priorityData = {
          priorityTitle: this.translateService.instant('notification.priority.high'),
          priorityClass: 'high',
        };
        break;
      case CompiereNotificationPriority.LOW:
        priorityData = {
          priorityTitle: this.translateService.instant('notification.priority.low'),
          priorityClass: 'low',
        };
        break;
      default:
        priorityData = {
          priorityTitle: this.translateService.instant('notification.priority.medium'),
          priorityClass: 'medium',
        };
        break;
    }
    return priorityData;
  }

  getReadableDate(notification: CompiereNotification): string {
    if (!notification.created) {
      return '';
    }

    const format = DateUtils.isSame(notification.created, Date.now()) ? 'LT' : 'L LT';
    return DateUtils.formatStr(notification.created, format);
  }

  /**
   * Défini l'icon pour les fichiers
   */
  private getIcons(notif: CompiereNotification): CompiereNotificationDataIcon[] {
    if (!notif.fileLinks) {
      return [];
    }
    return notif.fileLinks.map((fileLink) => ({
      fileLink: fileLink.path,
      icon: this.getFileIcon(fileLink.extension),
      fileName: fileLink.fileName,
    })) as CompiereNotificationDataIcon[];
  }

  /**
   * Obtiens l'icône par rapport à son extension
   * @param {string}fileLink
   */
  private getFileIcon(fileLink: string): string {
    const iconMap: { [extension: string]: string } = {
      pdf: 'icon-pdf',
      csv: 'icon-csv',
      txt: 'icon-txt',
      xls: 'icon-excel',
      xlsx: 'icon-excel',
      doc: 'icon-word',
      docx: 'icon-word',
      sql: 'icon-sql',
      zip: 'icon-zip',
      html: 'icon-html',
    };

    return iconMap[fileLink] || 'icon-file';
  }
}
